import { useCallback, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Star, Users, BedDouble, MapPin, Sparkles, ChevronLeft, ChevronRight, CalendarX } from "lucide-react";
import { fmtINR } from "@/lib/api";
import ShareStayButton from "@/components/common/ShareStayButton";
import useAxisLockedSwipe from "@/lib/useAxisLockedSwipe";

// Airbnb (OTA) rates run ~15% higher than our direct rate — surfaced as a soft "Save vs Airbnb"
// badge on every card so guests see the value of booking direct without the discount-site feel.
const AIRBNB_MARKUP = 0.15;

/**
 * PropertyCard — listing tile with a transform-based photo slider.
 *
 * Implementation details for the photo slider:
 *  - We do NOT use `overflow-x: scroll` + `scroll-snap`. Browsers (especially
 *    Chrome on Android) frequently lock the gesture into horizontal-only when
 *    the user starts a slightly-diagonal vertical swipe, which freezes page
 *    scrolling. We've shipped that bug twice; never again.
 *  - Instead, the track is a flex row translated by -active * 100% via JS.
 *    Touch handling is done in `useAxisLockedSwipe` which abandons the
 *    gesture on the first frame when |dy| >= |dx| — vertical page scroll then
 *    proceeds natively.
 *  - Click on a card's photo area still navigates to the property page (the
 *    whole card is wrapped in a <Link>). Arrow buttons stop propagation.
 */
export default function PropertyCard({ p, variant = "default" }) {
  const sp = typeof window !== "undefined" ? window.location.search : "";
  const images = [p.hero_image, ...(p.gallery || [])].filter(Boolean).slice(0, 8);
  const N = images.length;
  const [active, setActive] = useState(0);
  const trackWrapRef = useRef(null);

  const goTo = useCallback((idx, e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (!N) return;
    setActive((((idx % N) + N) % N));
  }, [N]);

  useAxisLockedSwipe({
    elementRef: trackWrapRef,
    onSwipe: (dir) => setActive((cur) => (((cur + dir) % N) + N) % N),
  });

  const unavailable = p.available === false;

  return (
    <Link
      to={`/stays/${p.slug}${sp}`}
      data-testid={`property-card-${p.slug}`}
      data-available={unavailable ? "no" : "yes"}
      className="group flex flex-col overflow-hidden rounded-2xl bg-white shadow-card transition-all hover:-translate-y-1 hover:shadow-soft"
    >
      <div
        ref={trackWrapRef}
        data-testid={`property-card-carousel-${p.slug}`}
        className="relative aspect-[4/3] overflow-hidden"
      >
        {/*
          IMPLEMENTATION NOTE (2026-04-30): we used to translate a flex track
          by -active*100%. On Chrome (Android + desktop) the compositor would
          intermittently fail to paint images that hadn't been on-screen at
          the first paint, leaving a BLANK WHITE frame after every swipe even
          though the imgs were fully loaded (naturalWidth/complete were true).
          Switching to absolute-positioned crossfades makes every image live
          inside the same painted layer from the first render — bulletproof
          across browsers and matches Airbnb's pattern. Swipe direction is
          still detected via useAxisLockedSwipe; we just no longer need the
          inline drag preview.
        */}
        {images.map((src, i) => (
          <img
            key={`${src}-${i}`}
            src={src}
            alt={`${p.public_name} ${i + 1}`}
            loading="eager"
            decoding="async"
            draggable={false}
            data-active={i === active ? "yes" : "no"}
            className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-500 ${
              i === active ? "opacity-100 z-[1]" : "opacity-0 z-0 pointer-events-none"
            }`}
          />
        ))}

        {N > 1 && (
          <>
            <div className="pointer-events-none absolute inset-x-0 bottom-3 z-10 flex justify-center gap-1.5">
              {images.map((src, i) => (
                <span
                  key={`dot-${src}-${i}`}
                  className={`h-1.5 rounded-full transition-all ${
                    i === active ? "w-5 bg-white" : "w-1.5 bg-white/60"
                  }`}
                />
              ))}
            </div>
            <button
              onClick={(e) => goTo(active - 1, e)}
              className="absolute left-2 top-1/2 z-10 -translate-y-1/2 rounded-full bg-black/40 p-1.5 text-white backdrop-blur transition hover:bg-black/55 md:opacity-0 md:group-hover:opacity-100"
              aria-label="Previous photo"
              data-testid={`property-card-prev-${p.slug}`}
            >
              <ChevronLeft size={14} />
            </button>
            <button
              onClick={(e) => goTo(active + 1, e)}
              className="absolute right-2 top-1/2 z-10 -translate-y-1/2 rounded-full bg-black/40 p-1.5 text-white backdrop-blur transition hover:bg-black/55 md:opacity-0 md:group-hover:opacity-100"
              aria-label="Next photo"
              data-testid={`property-card-next-${p.slug}`}
            >
              <ChevronRight size={14} />
            </button>
          </>
        )}

        {p.guest_favourite && !unavailable && (
          <div className="pointer-events-none absolute left-4 top-4 z-10 inline-flex items-center gap-1.5 rounded-full bg-parchment/90 px-3 py-1.5 text-[11px] font-medium uppercase tracking-wider text-forest backdrop-blur">
            <Sparkles size={12} className="text-gold" /> Guest Favourite
          </div>
        )}
        <div className="pointer-events-none absolute right-4 top-4 z-10 inline-flex items-center gap-1 rounded-full bg-noir/80 px-2.5 py-1 text-[11px] text-parchment backdrop-blur">
          <Star size={11} className="fill-gold text-gold" /> {p.rating?.toFixed?.(2) ?? p.rating} · {p.review_count}
        </div>
        <div className="absolute right-4 bottom-4 z-10">
          <ShareStayButton property={p} variant="icon" />
        </div>
        {unavailable && (
          <div className="pointer-events-none absolute left-4 top-4 z-10 inline-flex items-center gap-1.5 rounded-full bg-noir/85 px-3 py-1.5 text-[11px] font-medium uppercase tracking-wider text-parchment backdrop-blur" data-testid={`out-of-stock-badge-${p.slug}`}>
            <CalendarX size={12} className="text-gold" /> Unavailable for these dates
          </div>
        )}
      </div>
      <div className="flex flex-1 flex-col gap-3 p-6">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="font-display text-2xl text-noir leading-tight">{p.display_name}</h3>
            <p className="font-accent text-sm text-forest/70">{p.tagline}</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-3 text-xs text-noir/70">
          <span className="inline-flex items-center gap-1"><MapPin size={12} /> {p.location_label}</span>
          <span className="inline-flex items-center gap-1"><BedDouble size={12} /> {p.bedrooms} BR</span>
          <span className="inline-flex items-center gap-1"><Users size={12} /> {p.guests} guests</span>
        </div>
        {variant === "default" && !unavailable && (
          <p className="editorial-body text-sm line-clamp-3 !leading-snug">
            {p.short_description}
          </p>
        )}
        <div className="mt-auto flex items-end justify-between pt-2">
          {unavailable ? (
            <div className="flex flex-col">
              <p className="text-xs uppercase tracking-wider text-noir/60">Not open for your dates</p>
              <p className="font-display text-base text-forest">Check other dates →</p>
            </div>
          ) : (
            <div>
              <p className="text-xs uppercase tracking-wider text-amber">From</p>
              <p className="font-display text-xl text-forest">{fmtINR(p.nightly_price_from)} <span className="text-xs text-noir/60 font-sans">/ night</span></p>
              <p className="mt-1 text-[11px] text-forest/70" data-testid={`airbnb-savings-${p.slug}`}>
                Save ~{fmtINR(Math.round(p.nightly_price_from * AIRBNB_MARKUP))} vs Airbnb
              </p>
            </div>
          )}
          <span className="vyg-link-gold">{unavailable ? "See stay →" : "Explore the space →"}</span>
        </div>
      </div>
    </Link>
  );
}
